/**
 * Form field descriptor used for validation (e.g. mark-as-complete empty check).
 */
export interface FormFieldValidation {
  name: string;
  type: string;
  validation: boolean;
}

/** Minimal shape for a form page that has sections with rows and fields (for staticTableInput validation). */
interface FormPageSection {
  sections?: Array<{
    rows?: Array<{
      fields?: Array<{
        type?: string;
        name?: string;
        validations?: {
          notEmpty?: boolean;
          titleRequired?: boolean;
          atLeastOneRowComplete?: boolean;
          atLeastOneOfGroup?: string[];
        };
        requiredWhen?: Record<string, unknown>;
        columns?: Array<{
          required?: boolean;
          type?: string;
          value?: string;
          options?: unknown[];
          validations?: { notEmpty?: boolean };
          visibleWhen?: { field?: string; equals?: string };
        }>;
      }>;
    }>;
  }>;
}

type TemplateFormField = NonNullable<
  NonNullable<NonNullable<FormPageSection['sections']>[number]['rows']>[number]['fields']
>[number];

type FormPagesInput = FormPageSection | FormPageSection[] | undefined;

function normalizeFormPages(formPages: FormPagesInput): FormPageSection[] {
  if (!formPages) return [];
  return Array.isArray(formPages) ? formPages : [formPages];
}

function forEachTemplateField(
  formPages: FormPagesInput,
  visit: (field: TemplateFormField) => void | boolean
): void {
  for (const page of normalizeFormPages(formPages)) {
    for (const section of page.sections ?? []) {
      for (const row of section?.rows ?? []) {
        for (const field of row?.fields ?? []) {
          if (visit(field) === false) return;
        }
      }
    }
  }
}

const getMandatoryTableInputColumnValues = (
  formPages: FormPagesInput,
  tableFieldName: string
): Array<{ value: string; visibleWhen?: { field: string; equals: string } }> => {
  const values: Array<{ value: string; visibleWhen?: { field: string; equals: string } }> = [];
  forEachTemplateField(formPages, (field) => {
    if (field.type !== "tableInput" || field.name !== tableFieldName || !field.columns?.length) {
      return;
    }
    for (const col of field.columns) {
      if (col.required || col.validations?.notEmpty) {
        if (!col.value) continue;
        const visibleWhen = col.visibleWhen;
        values.push({
          value: col.value,
          ...(visibleWhen?.field && visibleWhen?.equals !== undefined
            ? { visibleWhen: { field: visibleWhen.field, equals: String(visibleWhen.equals) } }
            : {}),
        });
      }
    }
    return false;
  });
  return values;
};

const STATIC_DISPLAY_COLUMN_TYPES = new Set(['staticText', 'staticRichText']);

function findFormField(
  formPages: FormPagesInput,
  fieldName: string,
  fieldType: string
): TemplateFormField | undefined {
  let found: TemplateFormField | undefined;
  forEachTemplateField(formPages, (field) => {
    if (field.type === fieldType && field.name === fieldName) {
      found = field;
      return false;
    }
  });
  return found;
}

/** Cell considered filled for mark-complete checks (people picker stores user id string). */
export function isFilledFormCell(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    // Signature Clear uses "CLEARED"; in-progress uploads briefly use "uploading".
    // Do not treat "N/A" as empty — Pre-test Checklist uses it as a valid option,
    // and furnace requiredWhen-gated fields are skipped separately when unmet.
    if (
      trimmed === '' ||
      trimmed === 'CLEARED' ||
      trimmed === 'uploading'
    ) {
      return false;
    }
    return true;
  }
  if (typeof v === 'number') return !Number.isNaN(v);
  // Yes/No toggles: both true and false are intentional answers.
  if (typeof v === 'boolean') return true;
  // Time/datetime datepickers set a Date in RHF; saved values are usually ISO strings.
  // Without this, changing Start time falsely empties the field and disables Mark as complete.
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  if (typeof v === 'object') {
    const o = v as { _id?: unknown; id?: unknown };
    if (o._id != null && String(o._id).trim() !== '') return true;
    if (o.id != null && String(o.id).trim() !== '') return true;
  }
  return false;
}

const THERMOCOUPLE_BATCH_COLOUR_KEYS = [
  "thermocoupleBatchColour1",
  "thermocoupleBatchColour2",
  "thermocoupleBatchColour3",
  "thermocoupleBatchColour4",
  "thermocoupleBatchColour5",
  "thermocoupleBatchColour6",
] as const;

/** Unique groups from template `validations.atLeastOneOfGroup`; deduped by sorted key list. */
function collectAtLeastOneOfGroupsFromFormPages(formPages: FormPagesInput): string[][] {
  const groups: string[][] = [];
  const seen = new Set<string>();

  forEachTemplateField(formPages, (field) => {
    const g = field.validations?.atLeastOneOfGroup;
    if (!Array.isArray(g) || g.length === 0) return;
    const keys = g.map((k) => String(k).trim()).filter(Boolean);
    if (keys.length === 0) return;
    const sig = [...keys].sort().join("|");
    if (seen.has(sig)) return;
    seen.add(sig);
    groups.push(keys);
  });
  return groups;
}

/** True if template still has the six thermocouple text fields but no explicit atLeastOneOfGroup in JSON. */
function templateHasThermocoupleFieldsWithoutGroupRule(formPages: FormPagesInput): boolean {
  let hasColour1 = false;
  let hasGroupRule = false;
  forEachTemplateField(formPages, (field) => {
    if (field.name === "thermocoupleBatchColour1") {
      hasColour1 = true;
    }
    const g = field.validations?.atLeastOneOfGroup;
    if (Array.isArray(g) && g.some((k) => String(k).includes("thermocoupleBatchColour"))) {
      hasGroupRule = true;
    }
  });
  return hasColour1 && !hasGroupRule;
}

function atLeastOneGroupSatisfied(formData: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((key) => {
    const value = formData[key];
    return isFilledFormCell(value);
  });
}

function getEditableStaticTableColumnKeys(field: {
  columns?: Array<{ type?: string; value?: string }>;
}): string[] {
  const cols = field.columns ?? [];
  return cols
    .filter((c) => c.value && c.type && !STATIC_DISPLAY_COLUMN_TYPES.has(c.type))
    .map((c) => c.value as string);
}

function isRequiredWhenSatisfied(
  formData: Record<string, unknown>,
  requiredWhen?: Record<string, unknown>
): boolean {
  if (!requiredWhen || Object.keys(requiredWhen).length === 0) return true;
  return Object.keys(requiredWhen).every((key) => formData[key] === requiredWhen[key]);
}

/**
 * Returns true if any required (validation: true) field is empty, so the "mark as complete" button should be disabled.
 * `formPages` may be the current page or all pages (preferred for multi-tab forms).
 */
export function hasEmptyRequiredFields(
  formData: Record<string, unknown> | undefined,
  formFields: FormFieldValidation[],
  formPages: FormPagesInput
): boolean {
  if (!formData || typeof formData !== 'object') return true;

  // At least one of each `atLeastOneOfGroup` from the template (e.g. thermocouple batch colours).
  let groups = collectAtLeastOneOfGroupsFromFormPages(formPages);
  if (templateHasThermocoupleFieldsWithoutGroupRule(formPages)) {
    groups = [...groups, [...THERMOCOUPLE_BATCH_COLOUR_KEYS]];
  }
  for (const keys of groups) {
    if (!atLeastOneGroupSatisfied(formData, keys)) {
      return true;
    }
  }

  const requiredFields = formFields.filter(({ validation }) => validation);
  if (requiredFields.length === 0) return false;

  return !requiredFields.every(({ name, type }) => {
    const templateField = findFormField(formPages, name, type);
    // Fields gated by requiredWhen (e.g. non-selected furnace sections) are not required.
    if (templateField?.requiredWhen && !isRequiredWhenSatisfied(formData, templateField.requiredWhen)) {
      return true;
    }
    // Pre-test: people pickers paired with Complete/N/A checklist rows — not required when N/A.
    // Covers in-progress forms that may not yet have requiredWhen on the template field.
    if (typeof name === 'string' && name.endsWith('_people_picker')) {
      const pairedCheck = name.replace(/_people_picker$/, '');
      if (String(formData[pairedCheck] ?? '').trim() === 'N/A') {
        return true;
      }
    }

    const staticTableMandatoryColumns: string[] = [];
    if (type === 'staticTableInput') {
      const staticField = templateField || findFormField(formPages, name, 'staticTableInput');
      const staticValue = formData[name];
      if (staticField?.validations?.atLeastOneRowComplete) {
        if (!Array.isArray(staticValue) || staticValue.length === 0) return false;
        const keys = getEditableStaticTableColumnKeys(staticField);
        if (keys.length === 0) return false;
        return staticValue.some((row) =>
          keys.every((k) => isFilledFormCell((row as Record<string, unknown>)?.[k]))
        );
      }
      forEachTemplateField(formPages, (field) => {
        if (
          field.type === 'staticTableInput' &&
          field.validations?.notEmpty === true &&
          !!field.columns?.length &&
          field.name === name
        ) {
          for (const column of field.columns) {
            if (column.required) {
              const staticColumnsNumber = (field.columns || [])
                .filter(({ type: colType }) => colType === 'staticText')
                .map(({ options }) => (options?.length ?? 0));
              staticTableMandatoryColumns.push(
                ...Array(Math.max(...staticColumnsNumber, 0)).fill(column?.value)
              );
            }
          }
        }
      });
      const value = formData[name];
      if (value == null) return false;
      if (!Array.isArray(value)) return false;
      return (
        value.length !== 0 &&
        value.every((item) =>
          Object.entries(item || {}).every(([key, val]) => {
            if (staticTableMandatoryColumns.includes(key)) {
              return isFilledFormCell(val);
            }
            return true;
          })
        )
      );
    }
    if (type === 'imageCapture') {
      const photos = formData[name];
      const imgField = templateField || findFormField(formPages, name, 'imageCapture');
      const v = imgField?.validations || {};
      if (v.notEmpty) {
        if (!Array.isArray(photos) || photos.length === 0) return false;
      }
      if (v.titleRequired) {
        if (Array.isArray(photos) && photos.length > 0) {
          const missingTitle = photos.some((p) => !String((p as { title?: string })?.title ?? '').trim());
          if (missingTitle) return false;
        }
      }
      return true;
    }
    const value = formData[name];
    if (value == null) return false;

    if (type === "tableInput" && Array.isArray(value)) {
      const mandatoryCols = getMandatoryTableInputColumnValues(formPages, name);
      if (mandatoryCols.length > 0) {
        if (value.length === 0) return false;
        return value.every((row) =>
          mandatoryCols.every((col) => {
            const rowObj = (row as Record<string, unknown>) ?? {};
            if (col.visibleWhen) {
              const visibleFieldValue = rowObj[col.visibleWhen.field];
              if (String(visibleFieldValue ?? "").trim() !== String(col.visibleWhen.equals).trim()) {
                return true;
              }
            }
            const cell = rowObj[col.value];
            return isFilledFormCell(cell);
          })
        );
      }
    }

    return Array.isArray(value) ? value.length !== 0 : isFilledFormCell(value);
  });
}
